/**
 * useNewRecordDefaults — wartości startowe formularza NOWEGO rekordu kartoteki systemowej
 * (wartości domyślne pól ustawione w Ustawienia kartoteki → Pola), z `GET /api/record-defaults`.
 *
 * Zasady (plan „Wartości domyślne pól kartotek”, decyzje z 20.10 i 28.10):
 *  - formularz pokazuje pola dopiero po wczytaniu wartości (`status === 'ready'`) — wartości są
 *    widoczne od pierwszej chwili i nic nie nadpisuje tego, co użytkownik wpisze;
 *  - błąd wczytania → komunikat z „Spróbuj ponownie” (`NewRecordDefaultsStatus`), nie cichy
 *    formularz bez wartości;
 *  - „dziś” liczone w przeglądarce w chwili otwarcia formularza (`resolveDefaultValue`);
 *  - wartości trafiają także do pól, których nie ma na układzie formularza (stan formularza ma
 *    klucze wszystkich pól);
 *  - pole wypełnione przez link (np. kontrahent kontaktu z adresu) zostaje takie, jak przyszło —
 *    strona nakłada parametry z adresu PO wartościach domyślnych.
 *
 * Wartości są ZAMROŻONE przy pierwszym wczytaniu (odświeżenie zapytania w tle niczego nie zmienia
 * w otwartym formularzu); każde otwarcie formularza pobiera świeże ustawienia (`gcTime: 0`).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  type ParsedDefaultValue,
  type ResolvedDefaultValue,
  resolveDefaultValue,
} from '@saas/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { slugToCamel } from '@/lib/slug-to-camel';

interface RecordDefaultDto {
  slug: string;
  isCore: boolean;
  value: ParsedDefaultValue;
  withTime: boolean;
}

export interface NewRecordDefaults {
  /** `idle` — wyłączone (tryb edycji istniejącego rekordu). */
  status: 'idle' | 'loading' | 'error' | 'ready';
  /**
   * Pola systemowe: slug → wartość formularza. Dostępne o jeden commit WCZEŚNIEJ niż `ready`
   * (strona nakłada je w efekcie, zanim pokaże pola) — stabilna referencja po wczytaniu.
   */
  core: Readonly<Record<string, ResolvedDefaultValue>>;
  /** Pola dodatkowe: slug → wartość (stan `extValues`); stabilna referencja po wczytaniu. */
  ext: Readonly<Record<string, unknown>>;
  retry: () => void;
}

const EMPTY: Readonly<Record<string, never>> = Object.freeze({});

interface Frozen {
  core: Readonly<Record<string, ResolvedDefaultValue>>;
  ext: Readonly<Record<string, unknown>>;
}

function resolveAll(defaults: readonly RecordDefaultDto[], now: Date): Frozen {
  const core: Record<string, ResolvedDefaultValue> = {};
  const ext: Record<string, unknown> = {};
  for (const d of defaults) {
    const value = resolveDefaultValue(d.value, now, d.withTime);
    if (d.isCore) core[d.slug] = value;
    else ext[d.slug] = value;
  }
  return { core: Object.freeze(core), ext: Object.freeze(ext) };
}

/**
 * @param moduleSlug slug kartoteki systemowej (`purchase_invoices`, `contractors`…)
 * @param enabled `true` tylko w trybie tworzenia nowego rekordu
 */
export function useNewRecordDefaults(moduleSlug: string, enabled: boolean): NewRecordDefaults {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['record-defaults', moduleSlug],
    queryFn: async () => {
      const res = await api.get<{ defaults: RecordDefaultDto[] }>('/record-defaults', {
        params: { module: moduleSlug },
      });
      return res.data.defaults;
    },
    enabled,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });

  // Wartości zamrażane przy pierwszym wczytaniu w tej sesji formularza (w efekcie, nie w renderze).
  const [frozen, setFrozen] = useState<Frozen | null>(null);
  useEffect(() => {
    if (enabled && frozen === null && query.data) setFrozen(resolveAll(query.data, new Date()));
  }, [enabled, frozen, query.data]);

  // Strony nakładają wartości na stan formularza w efekcie (zależnym od `core`). Gdyby `ready`
  // przyszło w tym samym renderze co wartości, pierwsza klatka pokazałaby pola jeszcze bez nich.
  // Dlatego `core`/`ext` wychodzą od razu, a `ready` — o jeden commit później: efekt strony i ten
  // poniżej biegną w tym samym commicie, więc pola pojawiają się już z wartościami.
  const [settled, setSettled] = useState<Frozen | null>(null);
  useEffect(() => {
    if (frozen !== null && settled !== frozen) setSettled(frozen);
  }, [frozen, settled]);

  // Wyjście z trybu tworzenia (zapis → edycja, przejście na inny rekord): strona może się nie
  // przemontować (`/new` i `/:id` to ten sam komponent), a obserwator zapytania trzyma dane
  // mimo `gcTime: 0` — czyścimy je, żeby kolejne „Dodaj nowy” pobrało ustawienia od nowa.
  useEffect(() => {
    if (enabled) return;
    setFrozen(null);
    setSettled(null);
    queryClient.removeQueries({ queryKey: ['record-defaults', moduleSlug] });
  }, [enabled, moduleSlug, queryClient]);

  const { refetch } = query;
  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (!enabled) return { status: 'idle', core: EMPTY, ext: EMPTY, retry };
  if (frozen) {
    return { status: settled === frozen ? 'ready' : 'loading', ...frozen, retry };
  }
  if (query.isError) return { status: 'error', core: EMPTY, ext: EMPTY, retry };
  return { status: 'loading', core: EMPTY, ext: EMPTY, retry };
}

/**
 * Jawna zamiana kształtu wartości domyślnej na kształt pola formularza — dla pól, które
 * formularz trzyma inaczej niż wartość domyślna (liczba jako tekst, data jako pełny znacznik
 * czasu). Klucz = klucz stanu formularza.
 */
export type CoreDefaultConverters<TForm> = {
  [K in keyof TForm]?: (value: ResolvedDefaultValue) => TForm[K];
};

/** Liczba → tekst z kropką (pola kwot trzymane w formularzu jako tekst). */
export function numberAsString(value: ResolvedDefaultValue): string {
  return String(value);
}

/** Data `YYYY-MM-DD` → pełny znacznik czasu początku dnia (pola daty trzymane jako ISO). */
export function dateAsDayStartIso(value: ResolvedDefaultValue): string {
  return `${String(value).slice(0, 10)}T00:00:00.000Z`;
}

function sameShape(current: unknown, next: unknown): boolean {
  if (Array.isArray(current) || Array.isArray(next)) {
    return Array.isArray(current) && Array.isArray(next);
  }
  return typeof current === typeof next;
}

/**
 * Nakłada wartości domyślne pól systemowych na stan formularza: klucz stanu = `slugToCamel(slug)`
 * (konwencja seeda, jak `buildCoreFieldRecordValues`), kształt — przez `convert[klucz]`, gdy pole
 * trzyma wartość inaczej. Rozjazdy nie przechodzą po cichu:
 *  - slug bez pary w stanie formularza — ostrzeżenie w trybie deweloperskim (+ test strony
 *    `unmappedCoreDefaultSlugs`);
 *  - wartość innego kształtu niż bieżąca wartość pola (np. liczba do pola tekstowego bez
 *    `convert`) — błąd w trybie deweloperskim i w testach; w produkcji wpis w konsoli i pole bez
 *    wartości domyślnej.
 */
export function applyCoreDefaults<TForm extends object>(
  form: TForm,
  core: Readonly<Record<string, ResolvedDefaultValue>>,
  convert: CoreDefaultConverters<TForm> = {},
): TForm {
  const next: Record<string, unknown> = { ...(form as Record<string, unknown>) };
  const converters = convert as Partial<Record<string, (value: ResolvedDefaultValue) => unknown>>;
  for (const [slug, value] of Object.entries(core)) {
    const key = slugToCamel(slug);
    if (!(key in next)) {
      if (import.meta.env.DEV) {
        console.warn(`[record-defaults] pole „${slug}” nie ma klucza „${key}” w stanie formularza`);
      }
      continue;
    }
    const converter = converters[key];
    const nextValue = converter ? converter(value) : value;
    const current = next[key];
    if (current !== null && current !== undefined && !sameShape(current, nextValue)) {
      const message = `[record-defaults] pole „${slug}”: wartość domyślna innego kształtu niż pole formularza „${key}” — brak zamiany w \`convert\``;
      if (import.meta.env.DEV) throw new Error(message);
      console.error(message);
      continue;
    }
    next[key] = nextValue;
  }
  return next as TForm;
}

/** Slugi pól systemowych bez pary w stanie formularza (dla testów stron). */
export function unmappedCoreDefaultSlugs<TForm extends object>(
  form: TForm,
  slugs: readonly string[],
): string[] {
  return slugs.filter((slug) => !(slugToCamel(slug) in form));
}
